import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, CheckCircle, Brain } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Onboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState({ name: "", age: "", location: "" });

  useEffect(() => {
    // Get user from location state or localStorage
    const stateUser = location.state?.user;
    const storedUser = localStorage.getItem('heysolo_user');
    
    if (stateUser) {
      setUser(stateUser);
      setUserInfo(prev => ({ ...prev, name: stateUser.name || "" }));
    } else if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      setUserInfo(prev => ({ ...prev, name: parsed.name || "" }));
    } else {
      navigate('/');
      return;
    }

    fetchQuestions();
  }, [location, navigate]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/vibe-questions`);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
      // Fallback questions
      setQuestions([
        { id: 1, question: "What's your ideal way to spend a Saturday?", options: { a: "Adventure outdoors", b: "Cozy cafe with a book", c: "Brunch with friends", d: "Exploring new places" } },
        { id: 2, question: "How do you recharge after a long week?", options: { a: "Solo time at home", b: "Dinner with close friends", c: "Trying something new", d: "Deep conversations" } },
        { id: 3, question: "What type of travel excites you most?", options: { a: "Adventure trips", b: "Cultural exploration", c: "Beach relaxation", d: "City experiences" } }
      ]);
    }
    setLoading(false);
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    const surveyAnswers = Object.entries(answers).map(([questionId, answer]) => ({
      question_id: parseInt(questionId),
      answer: answer
    }));

    try {
      const sessionToken = localStorage.getItem('heysolo_session_token');
      const response = await axios.post(`${API}/survey/submit`, {
        name: userInfo.name || user?.name || "Solo Sistah",
        age: parseInt(userInfo.age) || 25,
        location: userInfo.location || "India",
        answers: surveyAnswers,
        open_response: ""
      }, {
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}
      });

      if (response.data.success) {
        localStorage.setItem('heysolo_user', JSON.stringify(response.data.user));
        navigate('/dashboard', { state: { user: response.data.user } });
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      // Navigate anyway for now
      navigate('/dashboard');
    }
    
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1DB89F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your vibe journey...</p>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const isAnswered = currentQ && answers[currentQ.id];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FFFE] to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-[#0A1A14]">
              <img 
                src="https://customer-assets.emergentagent.com/job_49fce761-7dfc-4661-b423-cf9714a9e8da/artifacts/kxs1eq2a_image.png"
                alt="Hey Solo Sistah"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-xl font-bold text-[#2D2D2D]">
              heysolo<span className="text-[#1DB89F]">.ai</span>
            </span>
          </div>
          <span className="text-sm text-gray-500">
            Question {currentQuestion + 1} of {questions.length}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-100 h-2">
        <div 
          className="bg-gradient-to-r from-[#1DB89F] to-[#0891B2] h-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* User Info Form (first screen) */}
      {currentQuestion === 0 && !userInfo.name && (
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#1DB89F]/10 px-4 py-2 rounded-full mb-4">
              <Brain className="w-5 h-5 text-[#1DB89F]" />
              <span className="text-[#1DB89F] font-semibold">10 Psychological Questions</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D2D2D] mb-2">
              Let's discover your <span className="text-[#1DB89F]">vibe!</span>
            </h1>
            <p className="text-gray-500">Tell us a bit about yourself first</p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name</label>
              <input
                type="text"
                value={userInfo.name}
                onChange={(e) => setUserInfo(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your name"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1DB89F] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your Age</label>
              <input
                type="number"
                value={userInfo.age}
                onChange={(e) => setUserInfo(prev => ({ ...prev, age: e.target.value }))}
                placeholder="25"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1DB89F] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your City</label>
              <input
                type="text"
                value={userInfo.location}
                onChange={(e) => setUserInfo(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Mumbai, Delhi, Bangalore..."
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1DB89F] focus:outline-none"
              />
            </div>
            <Button
              onClick={() => setUserInfo(prev => ({ ...prev, name: prev.name || user?.name || "Solo Sistah" }))}
              disabled={!userInfo.name}
              className="w-full bg-[#1DB89F] hover:bg-[#0A8F7F] text-white font-bold py-4 rounded-xl"
            >
              Start Questions
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Questions */}
      {(userInfo.name || currentQuestion > 0) && currentQ && (
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#0891B2]/10 px-4 py-2 rounded-full mb-4">
              <span className="text-[#0891B2] font-semibold">Question {currentQuestion + 1}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D2D2D]">
              {currentQ.question}
            </h2>
          </div>

          <div className="space-y-4">
            {Object.entries(currentQ.options).map(([key, value]) => (
              <button
                key={key}
                onClick={() => handleAnswer(currentQ.id, key)}
                className={`w-full p-5 rounded-2xl text-left transition-all border-2 ${
                  answers[currentQ.id] === key
                    ? 'border-[#1DB89F] bg-[#1DB89F]/10 shadow-lg'
                    : 'border-gray-200 bg-white hover:border-[#1DB89F]/50 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    answers[currentQ.id] === key
                      ? 'bg-[#1DB89F] text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {key.toUpperCase()}
                  </div>
                  <span className={`font-medium ${
                    answers[currentQ.id] === key ? 'text-[#1DB89F]' : 'text-gray-700'
                  }`}>
                    {value}
                  </span>
                  {answers[currentQ.id] === key && (
                    <CheckCircle className="w-6 h-6 text-[#1DB89F] ml-auto" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentQuestion === 0}
              className="px-6 py-3 rounded-xl"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Previous
            </Button>

            {currentQuestion < questions.length - 1 ? (
              <Button
                onClick={handleNext}
                disabled={!isAnswered}
                className="bg-[#1DB89F] hover:bg-[#0A8F7F] text-white px-6 py-3 rounded-xl"
              >
                Next
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!isAnswered || submitting}
                className="bg-gradient-to-r from-[#1DB89F] to-[#0891B2] text-white px-8 py-3 rounded-xl"
              >
                {submitting ? 'Finding your tribe...' : 'Discover My Tribe'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
